import "moment/locale/pt-br";
import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { useTour } from "hooks/useTour";
import {
  fetchConsumptionBills,
  fetchContestationById,
  fetchValidatedContacts,
  postContestation,
} from "../services/consumoApi";

export function useConsumoPage() {
  const [status, setStatus] = useState("");
  const [billsFormatted, setBillsFormatted] = useState<any[]>([]);
  const [firstBill, setFirstBill] = useState<any>();
  const [code, setCode] = useState<any>({
    value: "",
    error: "",
  });
  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [openModalBill, setOpenModalBill] = useState(false);
  const [openModalValidation, setOpenModalValidation] = useState(false);
  const [statusCalc, setStatusCalc] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [months, setMonths] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [protocol, setProtocol] = useState("");

  const selectedUnit = useSelector(
    // @ts-ignore
    (state) => state.selectUnitReducer.selectedUnit
  );

  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  useEffect(() => {
    moment.locale("pt-br");
  }, []);

  useEffect(() => {
    setTourType("consumo");
    setSteps(3);
  }, [showTour, setTourType, setSteps]);

  const getBills = async (): Promise<void> => {
    const { instanceId } = selectedUnit;
    const data = await fetchConsumptionBills(instanceId);

    if (data?.length) {
      setFirstBill(data[0]);
      const firstBillDate = data[0]?.measurementDate;
      const newMonths = data
        ?.map((item: any) => {
          return {
            value: moment(
              item?.measurementDate?.replaceAll("-", "/"),
              "YYYY-MM-DD"
            ).format("MM/YYYY"),
            label: moment(
              item?.measurementDate?.replaceAll("-", "/"),
              "YYYY-MM-DD"
            ).format("MMMM/YYYY"),
          };
        })
        .splice(0, 13);
      setMonths(newMonths);
      const newData = data
        ?.map((item: any, index: number) => {
          let monthValueColor = "#000fff";

          if (index === 0) {
            monthValueColor = "#00f00f";
          } else {
            const firstBillMonth = moment(firstBillDate).format("MM");
            const currentBillMonth = moment(item?.measurementDate).format("MM");
            if (firstBillMonth === currentBillMonth) {
              monthValueColor = "#00ffff";
            }
          }

          return {
            month: moment(
              item?.measurementDate?.replaceAll("-", "/"),
              "YYYY-MM-DD"
            ).format(""),
            monthValue: item?.kgConsumption.replace(",", "."),
            monthValueColor,
          };
        })
        .splice(0, 13)
        .reverse();
      setBillsFormatted(newData);
    } else {
      setBillsFormatted([]);
    }
  };

  const submitContestations = async (
    data: Record<string, unknown>
  ): Promise<boolean | undefined> => {
    const { instanceId } = selectedUnit;
    let success;

    try {
      const result = await postContestation(data, instanceId);
      const { status: httpStatus } = result;

      if (data?.completed && httpStatus === 201) {
        const id = result?.headers?.location?.split("contestations/")[1];
        const dataId = await fetchContestationById(id);
        setProtocol(dataId?.caseNumber ?? "");
        setOpenModalValidation((prev) => !prev);
      }

      success = true;
    } catch (error) {
      console.error(error);
      success = false;
    }

    return success;
  };

  const requestGetUserContacts = async () => {
    const list = await fetchValidatedContacts();
    setContacts(list);
  };

  useEffect(() => {
    (async () => {
      setStatus("loading");
      await getBills();
      await requestGetUserContacts();
      setStatus("ready");
    })();

    return () => {
      const codeValue = localStorage.getItem("codeValue");

      if (codeValue?.length === 8) {
        const measurement = codeValue;
        const body = {
          attachments: [],
          comments: "",
          months: "",
          measurement,
          completed: false,
        };

        submitContestations(body);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleModalInfo = (): void => {
    setOpenModalInfo((prevState) => !prevState);
  };

  const toggleModalBill = (): void => {
    setOpenModalBill((prevState) => !prevState);
  };

  const toggleModalValidation = (): void => {
    setOpenModalValidation((prevState) => !prevState);
  };

  const onChangeCode = (value: string): void => {
    localStorage.setItem("codeValue", value);
    setCode({
      value,
      error: "",
    });
  };

  const calculateKg = (): any => {
    const { value } = code;

    if (value?.length < 8) {
      return setCode((prevState: any) => {
        localStorage.setItem("codeValue", prevState?.value);
        return {
          ...prevState,
          error: "É necessário incluir todos os dígitos do medidor",
        };
      });
    }

    const int = parseInt(value?.split("").splice(0, 5).join(""), 10);
    const decimals = value?.split("").splice(5, 7).join("");
    const newValue = `${int}.${decimals}` as any;
    const newFirstBill = firstBill?.finalMeasurement.replace(",", ".");
    const calc = newValue - newFirstBill;
    let statusTemp;

    if (calc >= 0) {
      statusTemp = "success";
      setShowForm(false);
    } else {
      statusTemp = "error";
      setShowForm(true);
    }
    localStorage.setItem("statusCalc", statusTemp);
    return setStatusCalc(statusTemp);
  };

  const openForm = (): void => {
    setShowForm(true);
  };

  const toBase64 = (file: any): Promise<void> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () =>
        // @ts-ignore
        resolve(reader.result.replace(/data[:].*?\/.*?;base64,/, ""));
      reader.onerror = (error) => reject(error);
    });

  const handleSubmit = async (
    { months: monthsField, files, description }: any,
    { setSubmitting, resetForm }: any
  ): Promise<void> => {
    setSubmitting(true);
    const measurement = code?.value;
    let error = "";

    const body: Record<string, unknown> = {
      measurement,
      completed: true,
      attachments: [],
      comments: "",
      months: "",
    };

    if (monthsField?.length > 1) {
      body.months = monthsField?.join(",");
    } else {
      body.months = monthsField?.[0];
    }

    let filesLocal = files;
    if (filesLocal?.length) {
      const filesPromise = filesLocal?.map(async (item: File) => {
        const base64 = await toBase64(item);
        return {
          filename: item?.name,
          size: item?.size,
          data: base64,
        };
      });
      filesLocal = await Promise.all(filesPromise).then((values) => values);
      const size = filesLocal?.reduce(
        (acc: number, item: { size: number }) => acc + item.size,
        0
      );

      if (size > 5000000) {
        error = "O tamanho máximo dos arquivos é de 5MB";
      }
      body.attachments = filesLocal;
    }

    if (description) {
      body.comments = description;
    }

    try {
      if (!error) {
        await submitContestations(body);
        localStorage.setItem("codeValue", "");
        setCode({
          value: "",
          errors: "",
        });
        localStorage.setItem("statusCalc", "");
        setStatusCalc("");
        setShowForm(false);
        resetForm();
        setSubmitting(false);
      } else {
        throw new Error(error);
      }
    } catch (error: any) {
      setSubmitting(false);
      toast.error(error?.message);
    }
  };

  return {
    status,
    billsFormatted,
    firstBill,
    code,
    openModalInfo,
    openModalBill,
    openModalValidation,
    statusCalc,
    showForm,
    months,
    contacts,
    protocol,
    showTour,
    tourStep,
    tourType,
    toggleModalInfo,
    toggleModalBill,
    toggleModalValidation,
    onChangeCode,
    calculateKg,
    openForm,
    handleSubmit,
    requestGetUserContacts,
  };
}
